/**
 * Parse a `.canary.md` file into a SpecAst.
 *
 * The grammar is constrained enough that a hand-rolled parser stays small and
 * keeps the CLI bundle thin (no remark/unified pull-in). We tokenize lines,
 * split on H2 headings, and dispatch by section name. Frontmatter parses as
 * YAML; structured per-node bullets are parsed inline.
 *
 * @module spec/parse
 */

import yaml from 'yaml';
import type { AssertionSeverity } from '@chatsdet/types';
import { extractNodeId, stripHtmlComments } from './parse-line-metadata.js';
import {
  parseAttachments,
  parseDeclaredOutputs,
  parseInputs,
  parseScenarios,
} from './parse-sections.js';
import {
  type CredentialRef,
  type AttachmentDecl,
  type DeclaredOutput,
  type Frontmatter,
  FrontmatterSchema,
  type InputDecl,
  type JourneyNode,
  type Scenario,
  type SpecAst,
  type SpecIssue,
} from './grammar.js';

export { extractNodeId } from './parse-line-metadata.js';

const FRONTMATTER_FENCE = /^---\s*$/;

export type ParseResult =
  | { ok: true; ast: SpecAst; issues: SpecIssue[] }
  | { ok: false; issues: SpecIssue[] };

export function parseSpec(source: string): ParseResult {
  const issues: SpecIssue[] = [];
  const lines = source.split(/\r?\n/);

  // 1. Frontmatter
  const fmEnd = findFrontmatterEnd(lines);
  if (fmEnd === -1) {
    return fail([{ severity: 'error', message: 'Spec must begin with `---` frontmatter.' }]);
  }
  const rawFrontmatter = lines.slice(1, fmEnd).join('\n');
  let frontmatter: Frontmatter;
  try {
    const parsed = yaml.parse(rawFrontmatter) ?? {};
    frontmatter = FrontmatterSchema.parse(normalizeActor(parsed));
  } catch (err) {
    return fail([
      {
        severity: 'error',
        message: `Frontmatter is invalid: ${err instanceof Error ? err.message : String(err)}`,
        line: 1,
      },
    ]);
  }

  // 2. Body — split into H1 (title, ignored — frontmatter.name is authoritative)
  //    and H2 sections.
  const bodyStart = fmEnd + 1;
  const sections = splitH2Sections(lines.slice(bodyStart), bodyStart);

  let spirit: string | undefined;
  let preflight: string | undefined;
  let cleanup: string | undefined;
  let inputs: InputDecl[] = [];
  let scenarios: Scenario[] = [];
  let attachments: AttachmentDecl[] = [];
  let declaredOutputs: DeclaredOutput[] = [];
  let journey: JourneyNode[] = [];

  for (const section of sections) {
    const heading = section.heading.toLowerCase();
    if (heading.startsWith('spirit'))
      spirit = stripBlockquoteOrPlain(section.body).trim() || undefined;
    else if (heading.startsWith('preflight')) preflight = section.body.trim() || undefined;
    else if (heading.startsWith('cleanup')) cleanup = section.body.trim() || undefined;
    else if (heading.startsWith('inputs'))
      inputs = parseInputs(section.body, section.startLine, issues);
    else if (heading.startsWith('scenarios'))
      scenarios = parseScenarios(section.body, section.startLine, issues);
    else if (heading.startsWith('attachments'))
      attachments = parseAttachments(section.body, section.startLine, issues);
    else if (heading.startsWith('declared outputs'))
      declaredOutputs = parseDeclaredOutputs(section.body, section.startLine, issues);
    else if (heading.startsWith('journey'))
      journey = parseJourney(section.body, section.startLine, issues);
    else if (heading.startsWith('metadata')) {
      // Opaque round-trip block — ignored by parse for now (compiler handles it).
    } else {
      issues.push({
        severity: 'warn',
        message: `Unknown H2 section "${section.heading}" — ignored.`,
        line: section.startLine,
      });
    }
  }

  if (journey.length === 0) {
    issues.push({
      severity: 'error',
      message: 'Spec must include a `## Journey` section with at least one node.',
    });
  }

  if (issues.some((i) => i.severity === 'error')) return { ok: false, issues };

  return {
    ok: true,
    ast: {
      frontmatter,
      spirit,
      preflight,
      cleanup,
      inputs,
      scenarios,
      attachments,
      declaredOutputs,
      journey,
    },
    // Warn-severity issues collected while parsing (e.g. a bullet or line
    // that didn't match any known grammar and was dropped) — a spec can be
    // structurally valid (ok: true) while still having silently lost
    // content the author wrote. Callers that only look at `ast` are
    // unaffected; callers that want to warn about content loss (see
    // spec-tools.ts's dropped-content detection) need these.
    issues,
  };
}

function fail(issues: SpecIssue[]): ParseResult {
  return { ok: false, issues };
}

function findFrontmatterEnd(lines: string[]): number {
  if (!FRONTMATTER_FENCE.test(lines[0] ?? '')) return -1;
  for (let i = 1; i < lines.length; i++) if (FRONTMATTER_FENCE.test(lines[i])) return i;
  return -1;
}

/**
 * Parse a credential-reference string into its discriminated form. Accepts:
 *   "admin"                  -> { kind: 'template', role: 'admin' }
 *   "persona admin"          -> { kind: 'template', role: 'admin' }
 *   "template admin"         -> { kind: 'template', role: 'admin' }
 *   "credential admin-bob"   -> { kind: 'credential', name: 'admin-bob' }
 *   "seed-output qaUser"     -> { kind: 'seedOutput', ref: 'qaUser' }
 *
 * Used by both the frontmatter `actor:` field and mid-journey
 * `**Switch role to**` nodes so the two surfaces stay symmetric.
 */
export function parseCredentialRef(raw: string): CredentialRef {
  const trimmed = raw.trim();
  if (trimmed.startsWith('credential ')) {
    return { kind: 'credential', name: trimmed.slice('credential '.length).trim() };
  }
  if (trimmed.startsWith('seed-output ')) {
    return { kind: 'seedOutput', ref: trimmed.slice('seed-output '.length).trim() };
  }
  let explicitTemplatePrefix: 'template ' | 'persona ' | null = null;
  if (trimmed.startsWith('template ')) {
    explicitTemplatePrefix = 'template ';
  } else if (trimmed.startsWith('persona ')) {
    explicitTemplatePrefix = 'persona ';
  }
  const role = explicitTemplatePrefix
    ? trimmed.slice(explicitTemplatePrefix.length).trim()
    : trimmed;
  return { kind: 'template', role };
}

function normalizeActor(raw: Record<string, unknown>): Record<string, unknown> {
  const a = raw.actor;
  if (typeof a !== 'string') return raw;
  return { ...raw, actor: parseCredentialRef(a) };
}

type Section = { heading: string; body: string; startLine: number };

function splitH2Sections(lines: string[], offset: number): Section[] {
  const sections: Section[] = [];
  let current: { heading: string; body: string[]; startLine: number } | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const m = /^##\s+(.+?)\s*$/.exec(line);
    if (m) {
      if (current)
        sections.push({
          heading: current.heading,
          body: current.body.join('\n'),
          startLine: current.startLine,
        });
      current = { heading: m[1], body: [], startLine: offset + i + 1 };
      continue;
    }
    if (current) current.body.push(line);
    // Pre-first-H2 content (typically the H1 title) is ignored.
  }
  if (current)
    sections.push({
      heading: current.heading,
      body: current.body.join('\n'),
      startLine: current.startLine,
    });
  return sections;
}

function stripBlockquoteOrPlain(body: string): string {
  return body
    .split('\n')
    .map((l) => (l.startsWith('> ') ? l.slice(2) : l.startsWith('>') ? l.slice(1) : l))
    .join('\n');
}

/* ── ## Journey ──────────────────────────────────────────────────────── */

function parseJourney(body: string, startLine: number, issues: SpecIssue[]): JourneyNode[] {
  const lines = body.split('\n');
  return parseJourneyBlock(lines, 0, lines.length, /* baseIndent */ 0, startLine, issues).nodes;
}

type BlockResult = { nodes: JourneyNode[]; nextIndex: number };

function parseJourneyBlock(
  lines: string[],
  start: number,
  end: number,
  baseIndent: number,
  startLine: number,
  issues: SpecIssue[]
): BlockResult {
  const nodes: JourneyNode[] = [];
  let i = start;
  while (i < end) {
    const raw = lines[i];
    const trimmed = raw.trim();
    if (!trimmed) {
      i++;
      continue;
    }
    const indent = leadingSpaces(raw);
    if (indent < baseIndent) break;

    const lineNo = startLine + i;

    // Extract `<!-- @id <uuid> -->` from the line if present. The compiler
    // writes these back after a successful create; on recompile they key
    // the diff against the live workflow's node UUIDs.
    const { stripped: withoutNodeId, nodeId } = extractNodeId(trimmed);
    const stripped = stripHtmlComments(withoutNodeId);

    // Block-level lead-in detection. Lead-ins start with `**…**`.
    const leadInMatch = /^\*\*([^*]+)\*\*\s*(.*)$/.exec(stripped);
    if (!leadInMatch) {
      issues.push({
        severity: 'warn',
        message: `Unexpected line in Journey: "${trimmed}"`,
        line: lineNo,
      });
      i++;
      continue;
    }
    const leadIn = leadInMatch[1].trim();
    const remainder = leadInMatch[2].trim();
    const idMeta: { nodeId?: string; sourceLine?: number } = {
      sourceLine: lineNo,
      ...(nodeId ? { nodeId } : {}),
    };

    const lower = leadIn.toLowerCase();

    // ── If / Then / Else (condition) ─────────────────────────────────
    if (lower.startsWith('if')) {
      let expression = stripPunctuation(
        leadIn.replace(/^if\b\s*/i, '') + (remainder ? ' ' + remainder : '')
      ).trim();
      i++;
      // Multi-line condition: when the lead-in carries no inline expression,
      // the expression is the following blockquote (preserves newlines).
      if (!expression) {
        const bq = readBlockquote(lines, i, end);
        if (bq) {
          expression = bq;
          i += countBlockquoteLines(lines, i, end);
        }
      }
      // Look for **Then:** and optional **Else:**
      const childIndent = baseIndent + 2;
      let thenBranch: JourneyNode[] = [];
      let elseBranch: JourneyNode[] = [];
      while (i < end) {
        const nextRaw = lines[i];
        const nextTrim = nextRaw.trim();
        if (!nextTrim) {
          i++;
          continue;
        }
        if (leadingSpaces(nextRaw) < baseIndent) break;
        const branch = /^\*\*(then|else):\*\*\s*$/i.exec(nextTrim);
        if (!branch) break;
        i++;
        const sub = parseJourneyBlock(lines, i, end, childIndent, startLine, issues);
        if (branch[1].toLowerCase() === 'then') thenBranch = sub.nodes;
        else elseBranch = sub.nodes;
        i = sub.nextIndex;
      }
      nodes.push({ ...idMeta, kind: 'condition', expression, thenBranch, elseBranch });
      continue;
    }

    // ── Single-line nodes ────────────────────────────────────────────
    // The description blockquote may be separated from the lead-in by one or
    // more blank lines (common in hand-authored specs). Skip them when
    // detecting it, and fold the skipped blanks into the consumed count so
    // bullet collection starts *after* the blockquote. Without this, a blank
    // line before the `>` description makes readBlockquote miss it, leaving
    // readBullets pointed at the `>` line — so the action's bullets
    // (assertions, captures) are silently dropped.
    let descScan = i + 1;
    while (descScan < end && !lines[descScan].trim()) descScan++;
    const description = readBlockquote(lines, descScan, end);
    const descLineCount = description
      ? descScan - (i + 1) + countBlockquoteLines(lines, descScan, end)
      : 0;
    let consumedMain = 1 + descLineCount;

    if (lower.startsWith('navigate to')) {
      const url = stripPunctuation(remainder);
      nodes.push({ ...idMeta, kind: 'navigate', pageUrl: url || (description ?? '').trim() });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('switch role to')) {
      // Accept both `**Switch role to X**` (role inside the bold lead-in, what
      // `pull` emits) and `**Switch role to** X` (role outside the bold, more
      // natural to author by hand). For the latter, leadIn is exactly
      // `Switch role to` with no trailing whitespace for the `\s+` to match,
      // so the strip leaves the prefix intact — detect that and fall back to
      // remainder instead of the stripped string.
      const stripped = leadIn.replace(/^switch role to\s+/i, '').trim();
      const fromLeadIn = stripped && stripped.toLowerCase() !== 'switch role to' ? stripped : '';
      const rest = fromLeadIn || remainder;
      const credentialRef = parseCredentialRef(stripPunctuation(rest));
      nodes.push({ ...idMeta, kind: 'switchRole', credentialRef });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('wait')) {
      const dur = parseDuration(remainder || leadIn.replace(/^wait\s*/i, ''));
      if (dur === null) {
        issues.push({
          severity: 'error',
          message: `Could not parse Wait duration: "${remainder}"`,
          line: lineNo,
        });
      } else {
        nodes.push({ ...idMeta, kind: 'wait', durationSeconds: dur });
      }
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('end')) {
      const outcome = lower.includes('fail') ? 'fail' : 'pass';
      nodes.push({ ...idMeta, kind: 'end', outcome });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('seed')) {
      const specName =
        stripPunctuation(remainder.replace(/^[`"]|[`"]$/g, '')) ||
        stripPunctuation(leadIn.replace(/^seed\s*/i, ''));
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const opts = parseKvBullets(subBullets.bullets);
      const inputs = parseSeedInputBullets(subBullets.bullets);
      nodes.push({
        ...idMeta,
        kind: 'seed',
        specName,
        description,
        version:
          opts.version === 'latest' || opts.version === undefined
            ? (opts.version as 'latest' | undefined)
            : Number(opts.version),
        onFailure: opts.onFailure as 'block' | 'warn' | undefined,
        mode: opts.mode as 'recommended' | 'api' | 'browser' | undefined,
        apiTimeoutSeconds:
          opts.apiTimeoutSeconds === undefined ? undefined : Number(opts.apiTimeoutSeconds),
        scope: opts.scope as 'inline' | 'suite' | undefined,
        runTeardown:
          typeof opts.runTeardown === 'string' ? opts.runTeardown !== 'false' : undefined,
        inputs,
      });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('setup')) {
      const specName =
        stripPunctuation(remainder.replace(/^[`"]|[`"]$/g, '')) ||
        stripPunctuation(leadIn.replace(/^setup\s*/i, ''));
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const opts = parseKvBullets(subBullets.bullets);
      nodes.push({
        ...idMeta,
        kind: 'setup',
        specName,
        description,
        version:
          opts.version === 'latest' || opts.version === undefined
            ? (opts.version as 'latest' | undefined)
            : Number(opts.version),
        mode: opts.mode as 'inline' | 'suite' | undefined,
      });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('action')) {
      const title = titleFromDashedLeadIn(leadIn, 'action', remainder);
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const action = parseActionBullets(subBullets.bullets, lineNo, issues);
      nodes.push({ ...idMeta, kind: 'action', title, description, ...action });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('assert audio')) {
      const label = leadIn.replace(/^assert audio\s*/i, '').trim() || remainder;
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const opts = parseKvBullets(subBullets.bullets);
      nodes.push({
        ...idMeta,
        kind: 'audioAssertion',
        label,
        description,
        severity: (opts.severity as AssertionSeverity) ?? 'fail',
        prompt: typeof opts.prompt === 'string' ? opts.prompt : undefined,
        model: opts.model as 'gpt-audio' | 'gemini-2.x' | undefined,
        minDurationMs: numOpt(opts.minDurationMs),
        maxDurationMs: numOpt(opts.maxDurationMs),
        minRmsDb: numOpt(opts.minRmsDb),
        silenceFractionMax: numOpt(opts.silenceFractionMax),
        reference: typeof opts.reference === 'string' ? opts.reference : undefined,
        envelopeCorrelationMin: numOpt(opts.envelopeCorrelationMin),
      });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('assert')) {
      const condition =
        leadIn.replace(/^assert\s*/i, '').trim() + (remainder ? ' ' + remainder : '');
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const opts = parseKvBullets(subBullets.bullets);
      nodes.push({
        ...idMeta,
        kind: 'assertion',
        condition: stripPunctuation(condition),
        description,
        severity: (opts.severity as AssertionSeverity) ?? 'fatal',
        expectedOutcome:
          typeof opts.expectedOutcome === 'string' ? opts.expectedOutcome : undefined,
        alwaysUseAgent: opts.alwaysUseAgent === 'true' ? true : undefined,
      });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('play audio')) {
      const ref = leadIn.replace(/^play audio\s*/i, '').trim() || remainder;
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const opts = parseKvBullets(subBullets.bullets);
      nodes.push({
        ...idMeta,
        kind: 'audio',
        attachmentRef: stripPunctuation(ref),
        description,
        loop: opts.loop === 'true' ? true : undefined,
        waitForCompletion: opts.waitForCompletion === 'false' ? false : undefined,
        label: typeof opts.label === 'string' ? opts.label : undefined,
      });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('notify')) {
      const m = /^notify\s+([A-Za-z0-9_-]+)\s*:?\s*(.*)$/i.exec(
        leadIn + (remainder ? ' ' + remainder : '')
      );
      if (!m) {
        issues.push({
          severity: 'error',
          message: `Could not parse Notify lead-in: "${leadIn}"`,
          line: lineNo,
        });
      } else {
        const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
        consumedMain += subBullets.consumed;
        const opts = parseKvBullets(subBullets.bullets);
        nodes.push({
          ...idMeta,
          kind: 'notification',
          channel: m[1],
          to: stripPunctuation(stripBackticks(m[2])),
          subject: typeof opts.subject === 'string' ? opts.subject : undefined,
          body: description ?? '',
          attach: typeof opts.attach === 'string' ? opts.attach : undefined,
        });
      }
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('sandbox action')) {
      const slug = stripPunctuation(titleFromDashedLeadIn(leadIn, 'sandbox action', remainder));
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const opts = parseKvBullets(subBullets.bullets);
      const params = parseActionParamBullets(subBullets.bullets);
      nodes.push({
        ...idMeta,
        kind: 'sandboxAction',
        actionSlug: slug,
        description,
        label: typeof opts.label === 'string' ? opts.label : undefined,
        params,
        outputs:
          typeof opts.outputs === 'string'
            ? opts.outputs
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0)
            : undefined,
      });
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('mcp')) {
      const title = leadIn.replace(/^mcp\s*[—-]\s*/i, '').trim() || remainder;
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const opts = parseKvBullets(subBullets.bullets);
      warnUnknownBullets(
        subBullets.bullets,
        ['application', 'persona', 'outputs', 'tools'],
        'MCP',
        lineNo,
        issues
      );
      // Locked block format puts the instructions blockquote AFTER the
      // sub-bullets; accept a leading (description-slot) blockquote as a
      // fallback so hand-authored variants still parse.
      let instrScan = i + consumedMain;
      while (instrScan < end && !lines[instrScan].trim()) instrScan++;
      const trailing = readBlockquote(lines, instrScan, end);
      if (trailing) {
        consumedMain = instrScan - i + countBlockquoteLines(lines, instrScan, end);
      }
      const instructions = trailing ?? description ?? '';
      if (!title) {
        issues.push({
          severity: 'error',
          message: 'MCP node must carry a title: `**MCP — <title>**`.',
          line: lineNo,
        });
      }
      const application = typeof opts.application === 'string' ? opts.application : '';
      if (!application) {
        issues.push({
          severity: 'error',
          message: 'MCP node is missing its `- application: <MCP property name>` bullet.',
          line: lineNo,
        });
      }
      if (!instructions.trim()) {
        issues.push({
          severity: 'error',
          message: 'MCP node requires instructions — add a `> …` blockquote after the bullets.',
          line: lineNo,
        });
      }
      if (title && application && instructions.trim()) {
        nodes.push({
          ...idMeta,
          kind: 'mcp',
          title,
          application,
          instructions: instructions.trim(),
          // The trailing blockquote is the instructions, not a description; only
          // a leading blockquote that was NOT consumed as instructions remains a
          // description.
          description: trailing ? description : undefined,
          persona: typeof opts.persona === 'string' ? opts.persona : undefined,
          outputs: splitList(opts.outputs),
          tools: splitList(opts.tools),
        });
      }
      i += consumedMain;
      continue;
    }

    if (lower.startsWith('api sequence')) {
      const title = titleFromDashedLeadIn(leadIn, 'api sequence', remainder);
      const subBullets = readBullets(lines, i + 1 + descLineCount, end, baseIndent);
      consumedMain += subBullets.consumed;
      const opts = parseKvBullets(subBullets.bullets);
      nodes.push({
        ...idMeta,
        kind: 'apiSequence',
        title,
        apiEndpointContext: description ?? '',
        credentialInputName: typeof opts.credential === 'string' ? opts.credential : undefined,
        // Sidecar reference (e.g. `- apiSequence: ./sequences/x.yaml`).
        // Resolved by the CLI at compile/diff time relative to the spec file
        // path; parse.ts has no filesystem context so we just carry the string.
        sidecar: typeof opts.apiSequence === 'string' ? opts.apiSequence : undefined,
      });
      i += consumedMain;
      continue;
    }

    issues.push({ severity: 'error', message: `Unrecognized lead-in: "${leadIn}"`, line: lineNo });
    i++;
  }
  return { nodes, nextIndex: i };
}

function leadingSpaces(s: string): number {
  let n = 0;
  while (n < s.length && s[n] === ' ') n++;
  return n;
}

function stripPunctuation(s: string): string {
  return s.replace(/[`"]/g, '').replace(/\.$/, '').trim();
}

/**
 * Strip a `Keyword — Title` prefix from a lead-in, falling back to
 * `remainder` when the author instead wrote the title outside the bold span
 * (`**Keyword** Title`, the natural-to-hand-author form — mirrors the dual
 * forms `**Switch role to**` already accepts, see the comment above that
 * branch). In that form the em-dash-strip regex has nothing to match, so a
 * naive `.replace(...).trim() || remainder` leaves the bare keyword itself as
 * the "stripped" title (non-empty ⇒ truthy ⇒ the `|| remainder` fallback
 * never fires) and `remainder` — the actual title — is silently dropped.
 * Detects that case (stripped result empty, or equal to the bare keyword)
 * and uses `remainder` instead.
 */
function titleFromDashedLeadIn(leadIn: string, keyword: string, remainder: string): string {
  const stripped = leadIn.replace(new RegExp(`^${keyword}\\s*[—-]\\s*`, 'i'), '').trim();
  if (!stripped || stripped.toLowerCase() === keyword.toLowerCase()) return remainder;
  return stripped;
}

function stripBackticks(s: string): string {
  return s.replace(/`/g, '').trim();
}

function stripQuotes(s: string): string {
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1);
  }
  return s;
}

function parseDuration(s: string): number | null {
  const m = /^(\d+(?:\.\d+)?)\s*(s|m|h)?\.?$/i.exec(s.trim());
  if (!m) return null;
  const n = Number(m[1]);
  const unit = (m[2] ?? 's').toLowerCase();
  const seconds = unit === 'h' ? n * 3600 : unit === 'm' ? n * 60 : n;
  return Math.round(seconds);
}

function readBlockquote(lines: string[], start: number, end: number): string | undefined {
  if (start >= end) return undefined;
  if (!lines[start].trimStart().startsWith('>')) return undefined;
  const out: string[] = [];
  for (let i = start; i < end; i++) {
    const t = lines[i].trimStart();
    if (!t.startsWith('>')) break;
    out.push(t.replace(/^>\s?/, ''));
  }
  return out.join('\n').trim();
}

function countBlockquoteLines(lines: string[], start: number, end: number): number {
  let n = 0;
  for (let i = start; i < end; i++) {
    if (!lines[i].trimStart().startsWith('>')) break;
    n++;
  }
  return n;
}

function readBullets(
  lines: string[],
  start: number,
  end: number,
  baseIndent: number
): { bullets: string[]; consumed: number } {
  const bullets: string[] = [];
  let i = start;
  while (i < end) {
    const raw = lines[i];
    const trimmed = raw.trim();
    if (!trimmed) {
      i++;
      continue;
    }
    if (!trimmed.startsWith('- ')) break;
    if (leadingSpaces(raw) < baseIndent) break;
    bullets.push(trimmed.slice(2).trim());
    i++;
  }
  return { bullets, consumed: i - start };
}

/* ── Action sub-bullet parser ────────────────────────────────────────── */

function parseActionBullets(
  bullets: string[],
  lineNo: number,
  issues: SpecIssue[]
): {
  fields: { name: string; type?: string; value: string }[];
  capture: string[];
  assertions: { condition: string; severity: AssertionSeverity; agentic?: boolean }[];
  captureAudio?: boolean;
  captureLabel?: string;
  captureExtraMs?: number;
  alwaysUseAgent?: boolean;
} {
  const fields: { name: string; type?: string; value: string }[] = [];
  const capture: string[] = [];
  const assertions: { condition: string; severity: AssertionSeverity; agentic?: boolean }[] = [];
  let captureAudio: boolean | undefined;
  let captureLabel: string | undefined;
  let captureExtraMs: number | undefined;
  let alwaysUseAgent: boolean | undefined;

  for (const b of bullets) {
    // - field <name> [(type)]: <value>
    // <name> can be quoted, a bare identifier, or a plain multi-word label.
    const fieldM = /^field\s+(?:"([^"]+)"|(.+?))\s*(?:\(([^)]+)\))?\s*:\s*(.+)$/i.exec(b);
    if (fieldM) {
      const name = (fieldM[1] ?? fieldM[2] ?? '').trim();
      fields.push({
        name,
        type: fieldM[3],
        value: stripQuotes(fieldM[4].trim()),
      });
      continue;
    }
    // - capture: <name>  OR  - capture <name>
    const capM = /^capture\s*:?\s*(.+)$/i.exec(b);
    if (capM) {
      capture.push(stripQuotes(capM[1].trim()));
      continue;
    }
    // - assert (severity[, agentic]): <condition>
    const assertM = /^assert\s*\(([^)]+)\)\s*:\s*(.+)$/i.exec(b);
    if (assertM) {
      const flags = assertM[1].split(',').map((s) => s.trim().toLowerCase());
      const severity: AssertionSeverity = flags.includes('fatal')
        ? 'fatal'
        : flags.includes('fail')
          ? 'fail'
          : flags.includes('warn')
            ? 'warn'
            : 'fatal';
      const agentic = flags.includes('agentic') || undefined;
      assertions.push({ condition: stripQuotes(assertM[2].trim()), severity, agentic });
      continue;
    }
    const kv = /^([A-Za-z][A-Za-z0-9]*)\s*:\s*(.+)$/.exec(b);
    if (kv) {
      const key = kv[1];
      const value = kv[2].trim();
      if (key === 'captureAudio') captureAudio = value === 'true';
      else if (key === 'captureLabel') captureLabel = stripQuotes(value);
      else if (key === 'captureExtraMs') captureExtraMs = Number(value);
      else if (key === 'alwaysUseAgent') alwaysUseAgent = value === 'true';
      else
        issues.push({ severity: 'warn', message: `Unknown action option "${key}"`, line: lineNo });
      continue;
    }
    issues.push({
      severity: 'warn',
      message: `Could not parse action bullet: "${b}"`,
      line: lineNo,
    });
  }
  return {
    fields,
    capture,
    assertions,
    captureAudio,
    captureLabel,
    captureExtraMs,
    alwaysUseAgent,
  };
}

/* ── Generic key:value bullet parser ─────────────────────────────────── */

function parseKvBullets(bullets: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const b of bullets) {
    const m = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.+)$/.exec(b);
    if (!m) continue;
    out[m[1]] = stripQuotes(m[2].trim());
  }
  return out;
}

/**
 * Split a comma-separated bullet value (`outputs: a, b`) into a trimmed list.
 * Returns undefined for absent input or an empty result, so an omitted bullet
 * and an all-whitespace one produce the same AST.
 */
function splitList(v: string | undefined): string[] | undefined {
  if (typeof v !== 'string') return undefined;
  const items = v
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return items.length > 0 ? items : undefined;
}

/**
 * Warn on sub-bullets a node's grammar doesn't recognize (mirrors the action
 * bullet parser's unknown-option warnings). Non-`key: value` bullets and
 * unknown keys both warn — never silently dropped.
 */
function warnUnknownBullets(
  bullets: string[],
  known: readonly string[],
  nodeLabel: string,
  lineNo: number,
  issues: SpecIssue[]
): void {
  for (const b of bullets) {
    const m = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.+)$/.exec(b);
    if (!m) {
      issues.push({
        severity: 'warn',
        message: `Could not parse ${nodeLabel} bullet: "${b}"`,
        line: lineNo,
      });
      continue;
    }
    if (!known.includes(m[1])) {
      issues.push({
        severity: 'warn',
        message: `Unknown ${nodeLabel} option "${m[1]}"`,
        line: lineNo,
      });
    }
  }
}

/**
 * Parse `input.<name>: <value>` sub-bullets on a seed node into a value-input
 * override map. Dotted keys are intentionally outside parseKvBullets' grammar
 * so plain kv consumers never see them. Returns undefined when none present.
 */
function parseSeedInputBullets(bullets: string[]): Record<string, string> | undefined {
  let out: Record<string, string> | undefined;
  for (const b of bullets) {
    const m = /^input\.([A-Za-z_][A-Za-z0-9_]*)\s*:\s*(.+)$/.exec(b);
    if (!m) continue;
    out ??= {};
    out[m[1]] = stripQuotes(m[2].trim());
  }
  return out;
}

/**
 * Parse `param.<name>: <value>` sub-bullets on a sandbox action node into an
 * input-param map. Mirrors {@link parseSeedInputBullets}; dotted keys are
 * intentionally outside parseKvBullets' grammar. Returns undefined when none.
 */
function parseActionParamBullets(bullets: string[]): Record<string, string> | undefined {
  let out: Record<string, string> | undefined;
  for (const b of bullets) {
    const m = /^param\.([A-Za-z_][A-Za-z0-9_]*)\s*:\s*(.+)$/.exec(b);
    if (!m) continue;
    out ??= {};
    out[m[1]] = stripQuotes(m[2].trim());
  }
  return out;
}

function numOpt(v: string | undefined): number | undefined {
  if (v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}
